/**
 * Device fingerprinting for DYMAXION three-factor authentication.
 *
 * CRITICAL: This implementation MUST match Hub User Registration
 * EXACTLY. Any drift causes cross-domain fingerprint mismatch and
 * users are rejected at the Spoke login gate.
 *
 * Reference implementations:
 *   - /root/Archon/archon-ui-main/src/utils/deviceFingerprint.ts (Hub canonical)
 *   - login-service/public/landing.html (the version this port matches)
 *
 * Fingerprint string format (pipe-separated, EXACT ORDER):
 *   userAgent (normalised)
 *   | language
 *   | platform
 *   | hardwareConcurrency
 *   | screenResolution
 *   | timezone
 *   | colorDepth
 *   | touchSupport
 *   | cookieEnabled
 *   | doNotTrack
 *   | vendor
 *   | maxTouchPoints
 *
 * Excluded (intentional):
 *   - devicePixelRatio: Chrome stores zoom per-domain, breaks Hub↔Spoke match
 *   - userAgent version: auto-update churn breaks the hash
 *
 * Hashed via SubtleCrypto SHA-256 → 64-char lowercase hex.
 */
async function sha256(message) {
    // crypto.subtle is only exposed in a SecureContext — HTTPS or
    // localhost. Plain HTTP on a non-localhost origin returns undefined
    // and any call throws a cryptic TypeError. Fail fast with a clear
    // diagnostic so the auth layer can surface the real reason.
    if (typeof crypto === 'undefined' || !crypto.subtle) {
        throw new Error('SubtleCrypto is not available. DYMAXION three-factor auth requires a ' +
            'SecureContext (HTTPS or localhost). Reach this page via https:// or ' +
            'http://localhost to log in.');
    }
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}
/**
 * Strip version numbers from userAgent so browser auto-updates don't
 * invalidate the fingerprint on every minor version bump.
 */
function normalizeUserAgent(ua) {
    return ua.replace(/\/[\d.]+/g, '').replace(/\s+/g, ' ').trim();
}
function collectDeviceIdentifiers() {
    return {
        userAgent: normalizeUserAgent(navigator.userAgent),
        language: navigator.language,
        platform: navigator.platform,
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        colorDepth: window.screen.colorDepth,
        touchSupport: 'ontouchstart' in window,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack ?? 'null',
        vendor: navigator.vendor,
        maxTouchPoints: navigator.maxTouchPoints || 0,
    };
}
/**
 * Generate the canonical DYMAXION device fingerprint for the current
 * browser session. Must be called from a browser context (not SSR).
 * Returns a 64-char lowercase hex string, or empty string on failure.
 *
 * The empty-string fallback is intentional: the server validator will
 * reject it (length < 64) and the user will see an error, which is
 * preferable to passing a garbage value that might accidentally collide
 * with a registered device.
 */
export async function generateDeviceFingerprint() {
    const id = collectDeviceIdentifiers();
    // EXACT order matches Hub User Registration. Do not reorder.
    const fingerprintString = [
        id.userAgent,
        id.language,
        id.platform,
        id.hardwareConcurrency.toString(),
        id.screenResolution,
        id.timezone,
        id.colorDepth.toString(),
        id.touchSupport.toString(),
        id.cookieEnabled.toString(),
        id.doNotTrack || 'null',
        id.vendor,
        id.maxTouchPoints.toString(),
    ].join('|'); // CRITICAL: pipe separator, matches Hub registration
    // Let errors bubble up so the caller can surface them in the UI
    // (LoginPage wraps this in try/catch and renders the message in an
    // Alert). We no longer log to console.error here — repeated errors
    // on plain-HTTP review URLs (SecureContext unavailable) spammed the
    // devtools console. The real message still reaches the user via the
    // form-level Alert.
    return sha256(fingerprintString);
}
/** Validate fingerprint format — must be 64 lowercase hex chars. */
export function isValidDeviceFingerprint(fingerprint) {
    return /^[a-f0-9]{64}$/i.test(fingerprint);
}
//# sourceMappingURL=deviceFingerprint.js.map